import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingBag, Star, Package, Truck, Shield, CaretLeft, CaretRight, WhatsappLogo, Plus } from '@phosphor-icons/react';
import { useLenis } from 'lenis/react';
import { fetchProduct as fetchOneProduct, fetchProducts } from '../lib/api';
import { toast } from 'sonner';
import ProductCard from '../components/ProductCard';
import { getWhatsAppUrl } from '../components/WhatsAppButton';
import { DELIVERY_CHARGE, OFFER_TIERS, useCart } from '../lib/CartContext';
import SizeGuide from '../components/SizeGuide';

const ProductDetail = () => {
  const { id } = useParams();
  const lenis = useLenis();
  const { addItem, openDrawer } = useCart();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageZoom, setImageZoom] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  // Natural size of each gallery image so every photo shows in full, in its own shape.
  const [imgSizes, setImgSizes] = useState({});

  useEffect(() => {
    // Fix #2: always scroll to top when product page opens.
    // Lenis hijacks native scroll, so we need to use its instance for it to stick.
    if (lenis) {
      lenis.scrollTo(0, { immediate: true });
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    setActiveImageIdx(0);
    fetchProduct();
  }, [id, lenis]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const data = await fetchOneProduct(id);
      setProduct(data);
      // Default-select the smallest/cheapest size
      const sizes = data.sizes || [];
      if (sizes.length > 0) {
        const cheapest = [...sizes].sort((a, b) => a.price - b.price)[0];
        setSelectedSize(cheapest);
      }

      // Fetch related products
      const related = await fetchProducts({ category: data.category });
      setRelatedProducts(related.filter((p) => p.id !== id).slice(0, 4));
    } catch (error) {
      console.error('Error fetching product:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppOrder = () => {
    const whatsappNumber = (process.env.REACT_APP_WHATSAPP_NUMBER || '917416970039');
    const price = selectedSize ? selectedSize.price : product.price;
    const sizeLabel = selectedSize ? selectedSize.label : 'Standard';
    const message =
      `Hi, I want to order:\n\n` +
      `Product: ${product.name}\n` +
      `Category: ${product.category}\n` +
      `Size: ${sizeLabel}\n` +
      `Quantity: 1\n` +
      `Price: ₹${price}\n` +
      `Delivery: ₹${DELIVERY_CHARGE}\n` +
      `Total: ₹${Number(price) + DELIVERY_CHARGE}\n` +
      `Link: ${window.location.origin}/product/${product.id}\n\n` +
      `Please confirm availability.`;
    window.open(getWhatsAppUrl(whatsappNumber, message), '_blank');
  };

  const handleAddToCart = () => {
    if (!product || !selectedSize) return;
    addItem(product, selectedSize, 1);
    toast.success(`${product.name} (${selectedSize.label}) added to cart`, {
      action: { label: 'View cart', onClick: openDrawer },
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-foreground text-xl">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-muted-foreground text-xl">Product not found</div>
      </div>
    );
  }

  const galleryImages =
    product && product.images && product.images.length > 0
      ? product.images
      : product
        ? [product.image_url]
        : [];
  const activeImage = galleryImages[activeImageIdx] || (product && product.image_url);
  const hasMultipleImages = galleryImages.length > 1;

  const goToImage = (idx) => {
    const count = galleryImages.length;
    if (count === 0) return;
    setActiveImageIdx(((idx % count) + count) % count);
  };

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="product-detail-page">
      <div className="max-w-7xl mx-auto px-4">
        {/* Product Section */}
        <div className="grid lg:grid-cols-2 gap-12 mb-20">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-4"
          >
            <div
              className="relative overflow-hidden bg-secondary border border-border/40 rounded-sm cursor-zoom-in select-none"
              style={{
                aspectRatio: imgSizes[activeImage]
                  ? `${imgSizes[activeImage].w} / ${imgSizes[activeImage].h}`
                  : product.image_width && product.image_height
                    ? `${product.image_width} / ${product.image_height}`
                    : product.category === 'Split Posters'
                      ? '16 / 9'
                      : '3 / 4',
                maxHeight: '80vh',
                marginInline: 'auto',
              }}
              data-testid="product-image"
            >
              <AnimatePresence initial={false} mode="wait">
                <motion.img
                  key={activeImage}
                  src={activeImage}
                  alt={product.name}
                  loading="eager"
                  drag={hasMultipleImages ? 'x' : false}
                  dragConstraints={{ left: 0, right: 0 }}
                  dragElastic={0.2}
                  onDragEnd={(_, info) => {
                    if (!hasMultipleImages) return;
                    if (info.offset.x < -60) goToImage(activeImageIdx + 1);
                    else if (info.offset.x > 60) goToImage(activeImageIdx - 1);
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25 }}
                  onClick={() => setImageZoom(!imageZoom)}
                  onLoad={(e) => {
                    const img = e.currentTarget;
                    if (img.naturalWidth) {
                      setImgSizes((prev) =>
                        prev[activeImage] ? prev : { ...prev, [activeImage]: { w: img.naturalWidth, h: img.naturalHeight } },
                      );
                    }
                  }}
                  className={`w-full h-full object-contain transition-transform duration-500 ${
                    imageZoom ? 'scale-150' : 'scale-100'
                  }`}
                />
              </AnimatePresence>

              {hasMultipleImages && (
                <>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      goToImage(activeImageIdx - 1);
                    }}
                    className="hidden sm:flex absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center bg-black/70 hover:bg-black text-white rounded-full transition-all"
                    aria-label="Previous image"
                    data-testid="gallery-prev"
                  >
                    <CaretLeft size={20} weight="bold" />
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      goToImage(activeImageIdx + 1);
                    }}
                    className="hidden sm:flex absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center bg-black/70 hover:bg-black text-white rounded-full transition-all"
                    aria-label="Next image"
                    data-testid="gallery-next"
                  >
                    <CaretRight size={20} weight="bold" />
                  </button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                    {galleryImages.map((_, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          goToImage(idx);
                        }}
                        className={`h-1.5 rounded-full transition-all ${
                          idx === activeImageIdx
                            ? 'w-6 bg-white'
                            : 'w-1.5 bg-white/50 hover:bg-white/80'
                        }`}
                        aria-label={`Go to image ${idx + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}

              {product.trending && (
                <div className="absolute top-4 left-4 bg-white text-black text-xs font-medium px-3 py-1 rounded-sm">
                  TRENDING
                </div>
              )}
              {product.limited_edition && (
                <div className="absolute top-4 right-4 bg-red-600 text-white text-xs font-medium px-3 py-1 rounded-sm">
                  LIMITED EDITION
                </div>
              )}
            </div>

            {hasMultipleImages && (
              <div
                className="flex gap-2 overflow-x-auto pb-1 snap-x snap-mandatory"
                data-testid="gallery-thumbnails"
              >
                {galleryImages.map((src, idx) => (
                  <button
                    key={src + idx}
                    type="button"
                    onClick={() => goToImage(idx)}
                    className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 overflow-hidden rounded-sm border-2 transition-all snap-start ${
                      idx === activeImageIdx
                        ? 'border-white opacity-100'
                        : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                    aria-label={`Show image ${idx + 1}`}
                    data-testid={`thumbnail-${idx}`}
                  >
                    <img
                      src={src}
                      alt={`${product.name} view ${idx + 1}`}
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div>
              <h1
                className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground tracking-tight mb-3"
                data-testid="product-name"
              >
                {product.name}
              </h1>
              <p className="text-muted-foreground text-sm mb-4">Category: {product.category}</p>
              <div className="flex items-baseline gap-3">
                <p
                  className="font-heading font-black text-3xl text-foreground transition-all duration-300"
                  data-testid="product-price"
                >
                  ₹{selectedSize ? selectedSize.price : product.price}
                </p>
                {selectedSize && (
                  <span className="text-muted-foreground text-sm" data-testid="selected-size-label">
                    / {selectedSize.label}
                  </span>
                )}
              </div>
            </div>

            {/* Size Selector */}
            {product.sizes && product.sizes.length > 0 && (
              <div className="border-t border-border/40 pt-6" data-testid="size-selector">
                <div className="flex items-baseline justify-between mb-3">
                  <h3 className="font-heading font-bold text-lg text-foreground">Choose Size</h3>
                  <p className="text-xs text-muted-foreground">
                    Prices update instantly
                  </p>
                </div>
                <div className="flex flex-wrap gap-3">
                  {product.sizes.map((size) => {
                    const isSelected =
                      selectedSize && selectedSize.label === size.label;
                    return (
                      <button
                        key={size.label}
                        type="button"
                        onClick={() => setSelectedSize(size)}
                        className={`min-w-[88px] px-5 py-3 rounded-sm border text-center transition-all ${
                          isSelected
                            ? 'bg-white text-black border-white shadow-md scale-[1.02]'
                            : 'bg-secondary text-foreground/80 border-border/40 hover:border-foreground/60 hover:text-foreground'
                        }`}
                        data-testid={`size-option-${size.label.toLowerCase()}`}
                        aria-pressed={isSelected}
                      >
                        <div className="font-heading font-bold text-base leading-none mb-1">
                          {size.label}
                        </div>
                        <div className="text-xs opacity-80">₹{size.price}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <SizeGuide sizes={product.sizes} selected={selectedSize} />

            {/* Offers */}
            <div className="border-t border-border/40 pt-6" data-testid="product-offers">
              <h3 className="font-heading font-bold text-lg text-foreground mb-3">Offers</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-3 text-foreground/85">
                  <span aria-hidden>📦</span>
                  <span>Flat ₹{DELIVERY_CHARGE} delivery — same charge however many posters you order</span>
                </li>
                {OFFER_TIERS.map((t) => (
                  <li key={t.id} className="flex items-start gap-3 text-foreground/85">
                    <span aria-hidden>{t.icon}</span>
                    <span>
                      <span className="font-semibold text-foreground">{t.label}</span> on orders above ₹{t.threshold}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-border/40 pt-6">
              <h3 className="font-heading font-bold text-lg text-foreground mb-3">Description</h3>
              <p className="text-foreground/80 leading-relaxed" data-testid="product-description">
                {product.description}
              </p>
            </div>

            {/* Key Highlights */}
            <div className="border-t border-border/40 pt-6">
              <h3 className="font-heading font-bold text-lg text-foreground mb-4">Key Highlights</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-foreground/80">
                  <Star size={20} weight="fill" className="text-yellow-500" />
                  <span>High-quality print</span>
                </div>
                <div className="flex items-center gap-3 text-foreground/80">
                  <Package size={20} weight="fill" />
                  <span>Durable material</span>
                </div>
                <div className="flex items-center gap-3 text-foreground/80">
                  <Truck size={20} weight="fill" />
                  <span>Fast delivery</span>
                </div>
                <div className="flex items-center gap-3 text-foreground/80">
                  <Shield size={20} weight="fill" />
                  <span>Quality guaranteed</span>
                </div>
              </div>
            </div>

            {/* Trust Badge */}
            <div className="border border-border/40 bg-secondary rounded-sm p-4">
              <p className="text-foreground/80 text-sm text-center">
                🎉 <span className="font-semibold">1000+ Happy Customers</span> | ✨{' '}
                <span className="font-semibold">Premium Quality</span>
              </p>
            </div>

            {/* WhatsApp CTA */}
            {/* CTAs */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={handleAddToCart}
                className="w-full bg-secondary hover:bg-muted border border-border/40 text-foreground py-4 px-6 rounded-sm font-heading font-bold text-base flex items-center justify-center gap-2 transition-all active:scale-[0.98]"
                data-testid="add-to-cart-btn"
              >
                <Plus size={20} weight="bold" />
                Add to Cart
              </button>
              <button
                onClick={handleWhatsAppOrder}
                className="w-full bg-white text-black py-4 px-6 rounded-sm font-heading font-bold text-base flex items-center justify-center gap-2 hover:bg-white/90 transition-all shadow-lg active:scale-[0.98]"
                data-testid="whatsapp-checkout-btn"
              >
                <WhatsappLogo size={22} weight="fill" />
                Buy Now
              </button>
            </div>
            {selectedSize && (
              <p className="text-center text-xs text-muted-foreground -mt-2">
                {selectedSize.label} · ₹{selectedSize.price}
              </p>
            )}
          </motion.div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section data-testid="related-products-section">
            <h2 className="font-heading font-bold text-3xl sm:text-4xl text-foreground tracking-tight mb-8">
              Related Products
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 grid-flow-row-dense">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
